import { useMutation, useQuery } from '@apollo/client';
import {
  AlertService,
  AlertWarningIcon,
  AnchorIcon,
  Button,
  EarthIcon,
  ExpiryDateTimeline,
  MadLib,
  MiniTooltip,
  Select,
  SelectOption,
  Snackbar,
  TextInput,
  ToggleIcon,
  TrashIcon,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Prompt } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { SaveReservePriceSettings } from '../../../graphql/mutations';
import { ReservePriceOptionsQuery, ReservePriceSettingsQuery } from '../../../graphql/queries';
import { ReservePriceStrings } from '../../../string-resources';
import ReservePriceStrategyAnchors, { ReservePriceStrategyAnchorsKeysObject } from '../../items/reserve-price-strategy-anchors';
import ShelfLifeBracket from './../components/shelf-life-bracket';
import StepContainer from './../components/step-container';
import { MADLIB_TEMPLATES, ReservePriceConstants, SHELF_LIFE_BRACKET_OPTIONS } from './reserve-price-constants';
import errorCodes from './reserve-price-errors';
import {
  cleanReservePriceSettingsForMutation,
  combineClasses,
  getDefaultBracket,
  isAddExceptionBracketDisabled,
  normalizeBracketForDisplay,
  renderErrorMessages,
  renderExceptionParameterSelectOptions,
  renderNoShelfLifeInfo,
  renderTimelineError,
  sortOptionsByCategoryAndValue,
} from './reserve-price-helpers';
import styles from './reserve-price-styles';

const useStyles = createUseStyles(styles);

const ReservePriceSettings = () => {
  const classes = useStyles();
  const [reservePriceSettings, setReservePriceSettings] = useState();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [touched, setTouched] = useState(false);
  const [bracketsValid, setBracketsValid] = useState({});
  const { data, loading: loadingSettings } = useQuery(ReservePriceSettingsQuery, { fetchPolicy: 'network-only' });
  const [saveReservePriceSettings, { loading }] = useMutation(SaveReservePriceSettings);
  const { data: reservePriceOptionsData, loading: loadingOptions } = useQuery(ReservePriceOptionsQuery, {
    skip: !reservePriceSettings?.exceptions?.length && !reservePriceSettings?.exceptions?.[0]?.scope?.scopeBasis,
    variables: { exceptionProperty: reservePriceSettings?.exceptions?.[0]?.scope?.scopeBasis },
    fetchPolicy: 'network-only',
  });

  const bracketSelectOptions = useMemo(() => {
    const options = Object.values(SHELF_LIFE_BRACKET_OPTIONS);
    if (!reservePriceSettings?.hasShelfLifeInfo) {
      return [options[0]];
    }
    return options;
  }, [reservePriceSettings?.hasShelfLifeInfo]);

  const timelinesValid = useMemo(() => {
    const brackets = Object.keys(bracketsValid);
    return brackets.every((b) => bracketsValid[b] === '');
  }, [bracketsValid]);

  const mappedExceptions = useMemo(() => {
    if (!reservePriceSettings || !reservePriceSettings.exceptions || !reservePriceOptionsData?.reservePriceOptions) return [];
    const optionMap = new Map();
    reservePriceOptionsData.reservePriceOptions.forEach((opt) => {
      optionMap.set(opt._id, { text: opt.value, value: opt._id, category: opt.category, _id: opt._id });
    });
    return reservePriceSettings.exceptions.map((ex) => {
      let scopeValues = ex.scope?.scopeValues || [];
      if (scopeValues.length && typeof scopeValues[0] !== 'object') {
        scopeValues = scopeValues.map((id) => optionMap.get(id) || { value: id, text: id, _id: id });
      }
      const shelfLifeBrackets = [...ex.shelfLifeBrackets];
      return {
        ...ex,
        shelfLifeBrackets,
        scope: ex.scope ? { ...ex.scope, scopeValues } : undefined,
      };
    });
  }, [reservePriceSettings, reservePriceOptionsData?.reservePriceOptions]);

  // Handles initial load of reserve price settings
  useEffect(() => {
    // If we have the query result and haven't set state yet, initialize it
    if (data?.reservePriceSettings && !reservePriceSettings) {
      let shelfLifeBrackets = data.reservePriceSettings.shelfLifeBrackets || [];
      let exceptions = data.reservePriceSettings.exceptions || [];
      if (!data.reservePriceSettings.hasShelfLifeInfo && !shelfLifeBrackets.length && data.reservePriceSettings.primaryAnchor) {
        shelfLifeBrackets = [getDefaultBracket()];
      }
      shelfLifeBrackets = shelfLifeBrackets.map(normalizeBracketForDisplay);
      exceptions = exceptions.map((exc) => ({
        ...exc,
        shelfLifeBrackets: exc.shelfLifeBrackets.map(normalizeBracketForDisplay),
      }));
      // If SetOnUpload, set multiplier from first shelfLifeBracket
      if (data.reservePriceSettings.primaryAnchor === ReservePriceStrategyAnchorsKeysObject.SetOnUpload) {
        setReservePriceSettings({
          ...data.reservePriceSettings,
          exceptions,
          shelfLifeBrackets,
          multiplier: shelfLifeBrackets[0]?.multiplier || '',
        });
      } else {
        setReservePriceSettings({
          ...data.reservePriceSettings,
          exceptions,
          shelfLifeBrackets,
        });
      }
      return;
    }

    // If the site doesn't have shelf life info, add a default bracket (and exception bracket if needed) if there's an anchor selected
    if (reservePriceSettings && !reservePriceSettings.hasShelfLifeInfo) {
      if (reservePriceSettings.primaryAnchor && (!reservePriceSettings.shelfLifeBrackets || !reservePriceSettings.shelfLifeBrackets.length)) {
        setReservePriceSettings((prev) => ({
          ...prev,
          shelfLifeBrackets: [getDefaultBracket()],
        }));
        return;
      }
      if (
        reservePriceSettings.exceptions &&
        reservePriceSettings.exceptions.length &&
        reservePriceSettings.exceptions[0].scope?.scopeBasis &&
        (!reservePriceSettings.exceptions[0].shelfLifeBrackets || reservePriceSettings.exceptions[0].shelfLifeBrackets.length === 0)
      ) {
        setReservePriceSettings((prev) => {
          const newExceptions = [...prev.exceptions];
          newExceptions[0] = {
            ...newExceptions[0],
            shelfLifeBrackets: [getDefaultBracket()],
          };
          return { ...prev, exceptions: newExceptions };
        });
      }
    }
  }, [data, reservePriceSettings]);

  // Handles error state for when no reserve price options are available for the selected exception parameter.
  useEffect(() => {
    if (
      reservePriceSettings?.exceptions?.length &&
      reservePriceSettings.exceptions[0]?.scope?.scopeBasis &&
      reservePriceOptionsData &&
      Array.isArray(reservePriceOptionsData.reservePriceOptions) &&
      reservePriceOptionsData.reservePriceOptions.length === 0
    ) {
      setReservePriceSettings((prev) => {
        const exceptionErrors = prev?.exceptionErrors ? [...prev.exceptionErrors] : [];
        const errorCode = errorCodes.NO_RESERVE_PRICE_OPTIONS.code;
        if (!exceptionErrors.some((err) => err.code === errorCode)) {
          exceptionErrors.push({ code: errorCode, message: errorCodes.NO_RESERVE_PRICE_OPTIONS.message });
        }
        return { ...prev, exceptionErrors };
      });
    } else {
      setReservePriceSettings((prev) => {
        if (!prev?.exceptionErrors) return prev;
        const filtered = prev.exceptionErrors.filter((err) => err.code !== errorCodes.NO_RESERVE_PRICE_OPTIONS.code);
        if (filtered.length === prev.exceptionErrors.length) return prev;
        return { ...prev, exceptionErrors: filtered };
      });
    }
  }, [reservePriceOptionsData, reservePriceSettings?.exceptions]);

  const hideSuccessMessage = () => setShowSuccessMessage(false);

  const save = () => {
    const cleanedSettings = cleanReservePriceSettingsForMutation(reservePriceSettings);
    saveReservePriceSettings({ variables: { reservePriceSettings: cleanedSettings } })
      .then((res) => {
        if (res.data.saveReservePriceSettings.errors?.length > 0) {
          res.data.saveReservePriceSettings.errors.map((error) => {
            if (error.code === errorCodes.RESERVE_PRICE_PRIMARY_ANCHOR_REQUIRED || error.code === errorCodes.RESERVE_PRICE_MULTIPLIER_REQUIRED) {
              setReservePriceSettings((prev) => {
                const primaryAnchorErrors = prev?.primaryAnchorErrors ? [...prev.primaryAnchorErrors] : [];
                if (!primaryAnchorErrors?.filter((err) => err.code === error.code)?.length) primaryAnchorErrors.push(error);
                return { ...prev, primaryAnchorErrors };
              });
            }
            if (
              error.code === errorCodes.RESERVE_PRICE_SHELF_LIFE_REQUIRED ||
              error.code === errorCodes.RESERVE_PRICE_SHELF_LIFE_GAPS ||
              error.code === errorCodes.RESERVE_PRICE_SHELF_LIFE_NOT_COVERED ||
              error.code === errorCodes.RESERVE_PRICE_OVERLAPPING_BRACKETS
            )
              setReservePriceSettings((prev) => {
                const shelfLifeBracketErrors = prev?.shelfLifeBracketErrors ? [...prev.shelfLifeBracketErrors] : [];
                if (!shelfLifeBracketErrors?.length || !shelfLifeBracketErrors?.filter((err) => err.code === error.code)?.length)
                  shelfLifeBracketErrors.push(error);
                return { ...prev, shelfLifeBracketErrors };
              });

            if (
              error.code === errorCodes.RESERVE_PRICE_EXCEPTION_BRACKETS_REQUIRED ||
              error.code === errorCodes.RESERVE_PRICE_EXCEPTION_OVERLAPPING_BRACKETS
            )
              setReservePriceSettings((prev) => {
                const exceptionErrors = prev?.exceptionErrors ? [...prev.exceptionErrors] : [];
                if (!exceptionErrors?.length || !exceptionErrors?.filter((err) => err.code === error.code)?.length) exceptionErrors.push(error);
                return { ...prev, exceptionErrors };
              });
          });
        } else {
          setShowSuccessMessage(true);
          setTouched(false);
          setReservePriceSettings((prev) => {
            if (!prev) return prev;
            const cleaned = { ...prev };
            delete cleaned.primaryAnchorErrors;
            delete cleaned.shelfLifeBracketErrors;
            delete cleaned.exceptionErrors;
            return cleaned;
          });
        }
      })
      .catch((err) => {
        AlertService.alert({
          type: 'warning',
          message: err.message,
        });
      });
  };

  const setTimelineValidity = useCallback((bracketSet, isValid, reason) => {
    setBracketsValid((prev) => {
      return { ...prev, [bracketSet]: isValid ? '' : reason };
    });
  }, []);

  const setBracketRange = (selected, index) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newBrackets = [...prev.shelfLifeBrackets];
      const bracket = { ...newBrackets[index] };
      if (selected[0]) {
        if (selected[0].value === 'all') {
          delete bracket.min;
          delete bracket.max;
        }
        if (selected[0].value === 'greater') delete bracket.max;
        if (selected[0].value === 'less') delete bracket.min;
        newBrackets[index] = { ...bracket, selectedBracketRange: selected[0] };
      }
      return { ...prev, shelfLifeBrackets: newBrackets };
    });
  };

  const addBracket = () => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newBrackets = prev?.shelfLifeBrackets ? [...prev.shelfLifeBrackets] : [];
      newBrackets.push(getDefaultBracket());
      return { ...prev, shelfLifeBrackets: newBrackets };
    });
  };

  const setAnchor = (selected) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newAnchor = selected[0]?.value;
      // If switching from SetOnUpload to another anchor, clear shelfLifeBrackets and exceptions
      if (
        prev?.primaryAnchor === ReservePriceStrategyAnchorsKeysObject.SetOnUpload &&
        newAnchor !== ReservePriceStrategyAnchorsKeysObject.SetOnUpload
      ) {
        return { ...prev, primaryAnchor: newAnchor, shelfLifeBrackets: [], exceptions: [] };
      }
      // If switching to SetOnUpload, set multiplier from first shelfLifeBracket if present
      if (
        newAnchor === ReservePriceStrategyAnchorsKeysObject.SetOnUpload &&
        Array.isArray(prev?.shelfLifeBrackets) &&
        prev.shelfLifeBrackets.length > 0
      ) {
        return { ...prev, primaryAnchor: newAnchor, multiplier: prev.shelfLifeBrackets[0].multiplier };
      }
      return { ...prev, primaryAnchor: newAnchor };
    });
  };

  const setMultiplier = useCallback((value) => {
    setTouched(true);
    // noopengrep: javascript.lang.security.audit.incomplete-sanitization.incomplete-sanitization
    setReservePriceSettings((prev) => ({ ...prev, multiplier: value.replace('%', '') }));
  }, []);

  const setBracket = (value, field, index) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newBrackets = [...prev.shelfLifeBrackets];
      newBrackets[index] = { ...newBrackets[index], [field]: value };
      return { ...prev, shelfLifeBrackets: newBrackets };
    });
  };

  const setExceptionBracket = (value, field, bracketIndex, exceptionIndex) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = [...prev.exceptions];
      const newBrackets = [...newExceptions[exceptionIndex].shelfLifeBrackets] || [];
      newBrackets[bracketIndex] = { ...newBrackets[bracketIndex], [field]: value };
      newExceptions[exceptionIndex] = {
        ...newExceptions[exceptionIndex],
        shelfLifeBrackets: newBrackets,
      };
      return { ...prev, exceptions: newExceptions };
    });
  };

  const setExceptionParameter = (selected) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const scopeBasis = selected.length > 0 ? selected[0].value : '';
      return {
        ...prev,
        exceptions: [{ scope: { scopeBasis, scopeValues: [] }, shelfLifeBrackets: [] }],
      };
    });
  };

  const addException = () => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = prev?.exceptions ? [...prev.exceptions] : [];
      newExceptions.push({
        _id: uuid(),
        scope: {
          scopeBasis: prev?.exceptions?.length ? prev.exceptions[0].scope.scopeBasis : undefined,
          scopeValues: [],
        },
        shelfLifeBrackets: reservePriceSettings?.hasShelfLifeInfo ? [] : [getDefaultBracket()],
      });
      return { ...prev, exceptions: newExceptions };
    });
  };

  const removeShelfLifeBracket = (index) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newBrackets = [...prev.shelfLifeBrackets];
      newBrackets.splice(index, 1);
      return { ...prev, shelfLifeBrackets: newBrackets };
    });
  };

  const removeException = (index) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = prev.exceptions ? [...prev.exceptions] : [];
      newExceptions.splice(index, 1);
      return { ...prev, exceptions: newExceptions };
    });
  };

  const removeExceptionShelfLifeBracket = (exceptionIndex, bracketIndex) => {
    if (!reservePriceSettings?.hasShelfLifeInfo) return;
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = prev.exceptions ? [...prev.exceptions] : [];
      const newBrackets = [...newExceptions[exceptionIndex].shelfLifeBrackets];
      newBrackets.splice(bracketIndex, 1);
      newExceptions[exceptionIndex] = {
        ...newExceptions[exceptionIndex],
        shelfLifeBrackets: newBrackets,
      };
      return { ...prev, exceptions: newExceptions };
    });
  };

  const setExceptionScopeValues = (selectedItems, index) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = prev.exceptions ? [...prev.exceptions] : [];
      if (newExceptions.length)
        newExceptions[index] = {
          ...newExceptions[index],
          scope: { scopeBasis: newExceptions[index].scope.scopeBasis, scopeValues: selectedItems },
        };
      return { ...prev, exceptions: newExceptions };
    });
  };

  const renderMadlib = (bracket, index, exceptionIndex) => {
    const anchorLabel = ReservePriceStrategyAnchors.get(reservePriceSettings.primaryAnchor);
    const type = bracket.selectedBracketRange?.value;
    if (!type) return null;
    const isFlatRate = reservePriceSettings?.primaryAnchor === 'FlatRate';
    const template = isFlatRate ? MADLIB_TEMPLATES.flatRate[type] : MADLIB_TEMPLATES[type](anchorLabel);
    const values = {
      all: { multiplier: bracket.multiplier },
      between: { min: bracket.min, max: bracket.max, multiplier: bracket.multiplier },
      greater: { min: bracket.min, multiplier: bracket.multiplier },
      less: { max: bracket.max, multiplier: bracket.multiplier },
    }[type];
    return (
      <MadLib
        template={template}
        values={values}
        onChange={(value, field) =>
          exceptionIndex === undefined ? setBracket(value, field, index) : setExceptionBracket(value, field, index, exceptionIndex)
        }
      />
    );
  };

  const getSelectedPrimaryAnchor = useMemo(() => {
    return reservePriceSettings?.primaryAnchor
      ? {
          text: ReservePriceStrategyAnchors.get(reservePriceSettings.primaryAnchor),
          value: reservePriceSettings.primaryAnchor,
        }
      : null;
  }, [reservePriceSettings?.primaryAnchor]);

  const hasErrorCode = useCallback((errors, code) => Array.isArray(errors) && errors.some((error) => error.code === code), []);

  const getSelectedExceptionParameter = useMemo(() => {
    if (reservePriceSettings?.exceptions) {
      const basis = reservePriceSettings?.exceptions[0]?.scope?.scopeBasis;
      const option = data?.reservePriceSettings.exceptionParameters.find((ep) => ep.id === basis);
      if (option)
        return {
          text: option.name,
          value: option.id,
        };
      return {
        text: basis,
        value: basis,
      };
    }
    return {};
  }, [reservePriceSettings?.exceptions, data?.reservePriceSettings.exceptionParameters]);

  const setExceptionBracketRange = (selected, bracketIndex, exceptionIndex) => {
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = [...prev.exceptions];
      const newBrackets = [...newExceptions[exceptionIndex].shelfLifeBrackets];
      newBrackets[bracketIndex] = { ...newBrackets[bracketIndex], selectedBracketRange: selected[0] };
      newExceptions[exceptionIndex] = {
        ...newExceptions[exceptionIndex],
        shelfLifeBrackets: newBrackets,
      };
      return { ...prev, exceptions: newExceptions };
    });
  };

  const addExceptionBracket = (exceptionIndex, exception) => {
    if (!exception.scope?.scopeValues || exception.scope.scopeValues.length === 0) return;
    setTouched(true);
    setReservePriceSettings((prev) => {
      const newExceptions = [...prev.exceptions];
      const newBrackets = [...newExceptions[exceptionIndex].shelfLifeBrackets];
      newBrackets.push(getDefaultBracket());
      newExceptions[exceptionIndex] = {
        ...newExceptions[exceptionIndex],
        shelfLifeBrackets: newBrackets,
      };
      return { ...prev, exceptions: newExceptions };
    });
  };

  const isAddExceptionDisabled = useMemo(() => {
    return (
      !reservePriceSettings?.exceptions?.length ||
      (reservePriceSettings?.exceptions?.length && !reservePriceSettings?.exceptions[0]?.scope?.scopeBasis) ||
      !reservePriceOptionsData?.reservePriceOptions?.length
    );
  }, [reservePriceOptionsData?.reservePriceOptions?.length, reservePriceSettings?.exceptions]);

  const getPromptMessage = useCallback(() => {
    return reservePriceSettings?.primaryAnchorErrors?.length ||
      reservePriceSettings?.shelfLifeBracketErrors?.length ||
      reservePriceSettings?.exceptionErrors?.length
      ? ReservePriceConstants.invalidSettings
      : ReservePriceConstants.unsavedChanges;
  }, [reservePriceSettings]);

  const isStep2Disabled = !reservePriceSettings?.primaryAnchor || reservePriceSettings?.primaryAnchor === 'SetOnUpload';

  const isStep3Disabled =
    !reservePriceSettings?.primaryAnchor || !reservePriceSettings?.shelfLifeBrackets?.length || reservePriceSettings?.primaryAnchor === 'SetOnUpload';

  const step1ClassName = useMemo(
    () => combineClasses(classes.step, reservePriceSettings?.primaryAnchorErrors?.length && classes.stepError),
    [classes.step, classes.stepError, reservePriceSettings?.primaryAnchorErrors],
  );
  const step2ClassName = useMemo(
    () =>
      combineClasses(classes.step, isStep2Disabled && classes.disabled, reservePriceSettings?.shelfLifeBracketErrors?.length && classes.stepError),
    [classes.step, classes.disabled, classes.stepError, isStep2Disabled, reservePriceSettings?.shelfLifeBracketErrors],
  );

  const step3ClassName = useMemo(
    () => combineClasses(classes.step, isStep3Disabled && classes.disabled, reservePriceSettings?.exceptionErrors?.length && classes.stepError),
    [classes.step, classes.disabled, classes.stepError, isStep3Disabled, reservePriceSettings?.exceptionErrors],
  );

  const renderPrimaryAnchorError = useCallback(() => {
    if (!hasErrorCode(reservePriceSettings?.primaryAnchorErrors, errorCodes.RESERVE_PRICE_PRIMARY_ANCHOR_REQUIRED)) return null;
    return (
      <div className={classes.fieldError}>
        <AlertWarningIcon className={classes.errorIcon} />
        <span>{ReservePriceConstants.primaryAnchorRequired}</span>
      </div>
    );
  }, [reservePriceSettings?.primaryAnchorErrors, classes, hasErrorCode]);

  const renderSetOnUploadInput = useCallback(() => {
    if (reservePriceSettings?.primaryAnchor !== ReservePriceStrategyAnchorsKeysObject.SetOnUpload) return null;
    return (
      <div className={classes.inputContainer}>
        <TextInput
          labelText={ReservePriceConstants.percent}
          onChange={setMultiplier}
          value={`${accounting.formatNumber(reservePriceSettings?.multiplier)}%`}
          error={hasErrorCode(reservePriceSettings?.primaryAnchorErrors, errorCodes.RESERVE_PRICE_MULTIPLIER_REQUIRED)}
          id="percent-input"
        />
        {hasErrorCode(reservePriceSettings?.primaryAnchorErrors, errorCodes.RESERVE_PRICE_MULTIPLIER_REQUIRED) ? (
          <div className={classes.fieldError}>
            <AlertWarningIcon className={classes.errorIcon} />
            <span>{ReservePriceConstants.percentRequired}</span>
          </div>
        ) : null}
      </div>
    );
  }, [
    reservePriceSettings?.primaryAnchor,
    setMultiplier,
    reservePriceSettings?.multiplier,
    hasErrorCode,
    reservePriceSettings?.primaryAnchorErrors,
    classes,
  ]);

  const boundValidityCheckers = useMemo(() => {
    const checkers = new Map();
    checkers.set('default', setTimelineValidity.bind(this, 'default'));
    mappedExceptions.forEach((exp) => {
      checkers.set(exp._id, setTimelineValidity.bind(this, exp._id));
    });
    return checkers;
  }, [setTimelineValidity, mappedExceptions]);

  const renderTimelineIfBrackets = useCallback(() => {
    if (!reservePriceSettings?.shelfLifeBrackets?.length) return null;
    const error = bracketsValid['default'];
    return (
      <div className={classes.timelineContainer}>
        {renderTimelineError(error, classes.timelineError)}
        <span>{ReservePriceConstants.shelfLifeCoverage}</span>
        <ExpiryDateTimeline
          enforceGaps
          primaryAnchor={reservePriceSettings.primaryAnchor}
          brackets={reservePriceSettings.shelfLifeBrackets}
          checkValid={boundValidityCheckers.get('default')}
        />
      </div>
    );
  }, [reservePriceSettings?.shelfLifeBrackets, classes, reservePriceSettings?.primaryAnchor, boundValidityCheckers, bracketsValid]);

  const renderExceptionTimelineIfBrackets = useCallback(
    (mappedExceptions, index) => {
      if (!(mappedExceptions?.length && mappedExceptions[index]?.shelfLifeBrackets?.length)) return null;
      const error = bracketsValid[mappedExceptions._id];
      return (
        <div className={classes.timelineContainer}>
          {renderTimelineError(error, classes.timelineError)}
          <span>{ReservePriceConstants.shelfLifeCoverage}</span>
          <ExpiryDateTimeline
            primaryAnchor={reservePriceSettings.primaryAnchor}
            brackets={reservePriceSettings.shelfLifeBrackets}
            exceptionBrackets={mappedExceptions[index]?.shelfLifeBrackets || []}
            checkValid={boundValidityCheckers.get(mappedExceptions._id)}
            enforce
          />
        </div>
      );
    },
    [classes, reservePriceSettings?.primaryAnchor, reservePriceSettings?.shelfLifeBrackets, boundValidityCheckers, bracketsValid],
  );

  const renderNoExceptionsIfNone = useCallback(() => {
    if (mappedExceptions?.length) return null;
    return (
      <div className={classes.noExceptionsContainer}>
        <span className={classes.noExceptions}>{ReservePriceConstants.noExceptions}</span>
      </div>
    );
  }, [mappedExceptions, classes]);

  const sortedReservePriceSelectOptions = useMemo(() => {
    return (reservePriceOptionsData?.reservePriceOptions || []).slice().sort(sortOptionsByCategoryAndValue);
  }, [reservePriceOptionsData?.reservePriceOptions]);

  const usedReservePriceSelectOptions = useMemo(() => {
    return mappedExceptions.reduce((usedOptions, exception) => {
      exception.scope?.scopeValues.forEach((sv) => usedOptions.add(sv.value));
      return usedOptions;
    }, new Set());
  }, [mappedExceptions]);

  const renderReservePriceSelectOptions = useCallback(
    (currentSelectedOptions) => {
      const currentOptions = currentSelectedOptions?.reduce((selected, option) => {
        selected.add(option.value);
        return selected;
      }, new Set());
      return sortedReservePriceSelectOptions
        .filter((opt) => {
          if (currentOptions?.has(opt._id)) return true;
          if (usedReservePriceSelectOptions?.has(opt._id)) return false;
          return true;
        })
        .map((v) => (
          <SelectOption key={v._id} value={v._id} category={v.category} searchText={`${v.value} ${v.category}`} checkbox>
            {v.value}
          </SelectOption>
        ));
    },
    [sortedReservePriceSelectOptions, usedReservePriceSelectOptions],
  );

  const renderAnchorSelectOptions = () =>
    Array.from(ReservePriceStrategyAnchors.keys()).map((k) => (
      <SelectOption key={k} value={k}>
        {ReservePriceStrategyAnchors.get(k)}
      </SelectOption>
    ));

  const renderExceptionShelfLifeBrackets = (exception, index) => (
    <>
      {exception.shelfLifeBrackets?.length
        ? exception.shelfLifeBrackets.map((bracket, bracketIndex) => (
            <ShelfLifeBracket
              key={`exception-${index}-bracket-${bracketIndex}`}
              bracket={bracket}
              index={bracketIndex}
              onChange={(selected) => setExceptionBracketRange(selected, bracketIndex, index)}
              onRemove={reservePriceSettings?.hasShelfLifeInfo ? () => removeExceptionShelfLifeBracket(index, bracketIndex) : undefined}
              renderMadlib={(b, i) => <div className={classes.madlibContainer}>{renderMadlib(b, i, index)}</div>}
              selectOptions={bracketSelectOptions}
              selectClassName={classes.selectSmall}
              deleteDisabled={!reservePriceSettings?.hasShelfLifeInfo}
            />
          ))
        : null}
      {reservePriceSettings?.hasShelfLifeInfo && (
        <div
          className={`${classes.stepActionContainer} ${isAddExceptionBracketDisabled(exception) ? classes.addBracketDisabled : ''}`}
          onClick={() => addExceptionBracket(index, exception)}
        >
          <span className={classes.addBracket}>{ReservePriceConstants.addBracket}</span>
        </div>
      )}
    </>
  );

  const renderExceptions = () => (
    <>
      {mappedExceptions?.map((exception, index) => (
        <div key={`exception-${index}`} className={classes.exceptionBracketContainer}>
          <div className={classes.exceptionHeaderContainer}>
            <Select
              containerClassName={classes.select}
              search
              multiple
              selectAll
              autoWidth
              categories={exception.scope?.scopeBasis === ReservePriceConstants.Category}
              selectedItems={exception.scope.scopeValues || []}
              label={`Select ${getSelectedExceptionParameter.text}`}
              onChange={(selected) => setExceptionScopeValues(selected, index)}
            >
              {renderReservePriceSelectOptions(exception.scope.scopeValues || [])}
            </Select>
            <MiniTooltip text="Delete exception" position="bottom">
              <div onClick={() => removeException(index)} data-testid={`exception-${index}-trash`}>
                <TrashIcon className={classes.trash} />
              </div>
            </MiniTooltip>
          </div>
          <div className={classes.descriptionContainer}>
            <span className={classes.stepActionText}>{ReservePriceConstants.step2Action}</span>
            <span className={classes.description}>{ReservePriceConstants.step2SubDescription}</span>
          </div>
          {renderExceptionTimelineIfBrackets(mappedExceptions, index)}
          {renderNoShelfLifeInfo(reservePriceSettings?.hasShelfLifeInfo, reservePriceSettings?.primaryAnchor, classes)}
          {renderErrorMessages(reservePriceSettings?.exceptionErrors, classes)}
          <div className={classes.bracketContainer}>{renderExceptionShelfLifeBrackets(exception, index)}</div>
        </div>
      ))}
      {renderNoExceptionsIfNone()}
    </>
  );

  const renderStandardShelfLifeBrackets = () => (
    <>
      {reservePriceSettings?.shelfLifeBrackets?.map((bracket, index) => (
        <ShelfLifeBracket
          key={`bracket-${index}`}
          bracket={bracket}
          index={index}
          onChange={setBracketRange}
          onRemove={reservePriceSettings?.hasShelfLifeInfo ? removeShelfLifeBracket : undefined}
          renderMadlib={(b, i) => <div className={classes.madlibContainer}>{renderMadlib(b, i)}</div>}
          selectOptions={bracketSelectOptions}
          selectClassName={classes.selectSmall}
          deleteDisabled={!reservePriceSettings?.hasShelfLifeInfo}
        />
      ))}
      {reservePriceSettings?.hasShelfLifeInfo && (
        <div className={classes.stepActionContainer} onClick={addBracket}>
          <span className={classes.addBracket}>{ReservePriceConstants.addBracket}</span>
        </div>
      )}
    </>
  );

  return !loadingSettings || (reservePriceSettings?._id && !loadingOptions) ? (
    <>
      <div className={classes.wrapper}>
        <h1 className={classes.title}>{ReservePriceConstants.title}</h1>
        <p className={classes.description}>{ReservePriceConstants.description}</p>
        <div className={classes.stepContainer}>
          <StepContainer
            stepNumber={1}
            title={ReservePriceConstants.step1Title}
            description={ReservePriceConstants.step1Description}
            icon={AnchorIcon}
            className={step1ClassName}
          >
            <div className={classes.anchorContainer}>
              <div>
                <Select
                  label={`${ReservePriceStrings.reservePriceAnchor} *`}
                  onChange={setAnchor}
                  selectedItem={getSelectedPrimaryAnchor}
                  placeholderText="Select an anchor"
                  containerClassName={classes.select}
                  error={hasErrorCode(reservePriceSettings?.primaryAnchorErrors, errorCodes.RESERVE_PRICE_PRIMARY_ANCHOR_REQUIRED)}
                >
                  {renderAnchorSelectOptions()}
                </Select>
                {renderPrimaryAnchorError()}
              </div>
              {renderSetOnUploadInput()}
            </div>
          </StepContainer>
          <StepContainer
            stepNumber={2}
            title={ReservePriceConstants.step2Title}
            description={ReservePriceConstants.step2Description}
            icon={EarthIcon}
            className={step2ClassName}
          >
            <div className={classes.descriptionContainer}>
              <span className={classes.stepActionText}>{ReservePriceConstants.step2Action}</span>
              <span className={classes.description}>{ReservePriceConstants.step2SubDescription}</span>
            </div>
            {reservePriceSettings?.primaryAnchor === ReservePriceStrategyAnchorsKeysObject.SetOnUpload ? null : (
              <>
                {renderNoShelfLifeInfo(reservePriceSettings?.hasShelfLifeInfo, reservePriceSettings?.primaryAnchor, classes)}
                {renderErrorMessages(reservePriceSettings?.shelfLifeBracketErrors, classes)}
                {renderTimelineIfBrackets()}
                <div className={classes.bracketContainer}>{renderStandardShelfLifeBrackets()}</div>
              </>
            )}
          </StepContainer>
          <StepContainer
            stepNumber={3}
            title={ReservePriceConstants.step3Title}
            description={ReservePriceConstants.step3Description}
            icon={ToggleIcon}
            className={step3ClassName}
          >
            <Select
              label={ReservePriceConstants.baseExceptionLabel}
              onChange={setExceptionParameter}
              selectedItem={getSelectedExceptionParameter}
              placeholderText={ReservePriceConstants.selectParameter}
              containerClassName={classes.select}
              id="exception-parameter"
            >
              {renderExceptionParameterSelectOptions(data?.reservePriceSettings.exceptionParameters)}
            </Select>
            <div className={classes.subDescriptionContainer}>
              <div className={classes.descriptionContainer}>
                <span className={classes.stepActionText}>{ReservePriceConstants.step3Action}</span>
                <span className={classes.description}>{ReservePriceConstants.step3SubDescription}</span>
              </div>
              <Button secondary onClick={addException} className={classes.addExceptionButton} disabled={isAddExceptionDisabled}>
                {ReservePriceConstants.addException}
              </Button>
            </div>
            <div className={classes.exceptionContainer}>{renderExceptions()}</div>
          </StepContainer>
        </div>
      </div>
      <div className={classes.saveSettings}>
        <Button
          onClick={save}
          primary
          disabled={!touched || !timelinesValid}
          loading={loading}
          loadingText={ReservePriceConstants.saving}
          testId="save"
        >
          {ReservePriceConstants.save}
        </Button>
      </div>
      <Snackbar show={showSuccessMessage} callback={hideSuccessMessage} message={ReservePriceConstants.saved} />
      <Prompt when={touched} message={getPromptMessage} />
    </>
  ) : null;
};

ReservePriceSettings.propTypes = {
  user: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  style: PropTypes.object,
  refetchQueries: PropTypes.array,
  history: PropTypes.object,
};

export default ReservePriceSettings;
